import { DerivedAvailabilityStatus } from '@commerce-product-webcomponents/sdk';
import { type IPProduct } from '@commerce-payment-webcomponents/sdk';
import { formatPrice } from '@nextgen-web-framework/core';
import {
  AmwayUserDetailsStatusEnum,
  Cart,
  DeliveryType,
  SopElementResponse,
  ExceptionsEnum,
  PromotionResult,
  AmwayUserDetails,
} from '../services';
import { CartEntryDetails } from '../services/models/CartEntryDetails';
import { SubCartDetail } from '../services/models/SubCartDetail';
import { getFilteredEntries } from './cartCommonHelpers';
import { getBundleEntriesAndChildPromoitions } from './cartLineItemsHelper';

export const freeShipingFromFulfillment = (data: Cart) => Boolean(data.shippingFee?.freeDeliveryAmount);

export const checkOutOfStockProduct = (cartEntries: CartEntryDetails[]) => {
  const notFreeItems = cartEntries?.filter((entry) => !entry.isFreeItem);
  const index = notFreeItems?.findIndex(
    (entry) =>
      entry.product.derivedAvailabilityStatus === DerivedAvailabilityStatus.OutOfStock ||
      entry.product.derivedAvailabilityStatus === DerivedAvailabilityStatus.Nla ||
      entry.product.derivedAvailabilityStatus === DerivedAvailabilityStatus.Nya,
  );

  return cartEntries ? index !== -1 : false;
};

export const checkIfFreeGiftOutOfStock = (cartEntries: CartEntryDetails[]) => {
  const freeItems = cartEntries?.filter((entry) => entry.isFreeItem);

  const index = freeItems?.findIndex(
    (entry) =>
      entry.product.derivedAvailabilityStatus === DerivedAvailabilityStatus.OutOfStock ||
      entry.product.derivedAvailabilityStatus === DerivedAvailabilityStatus.Nla,
  );

  return cartEntries ? index !== -1 : false;
};

export const calculateWidthProgressbar = (data: Cart) => {
  let freePercentage = 0;

  const totalPrice = Number(data.totalPriceWithoutServiceItems?.value) || 0;
  const freeDeliveryAmount = Number(data.shippingFee?.freeDeliveryAmount) || 0;

  if (freeDeliveryAmount > 0) {
    freePercentage = (totalPrice / freeDeliveryAmount) * 100;
  }
  return freePercentage;
};

export const priceForShippingText = (data: Cart) => {
  const str = formatPrice(
    Number(data.shippingFee?.freeDeliveryAmount ?? 0) - Number(data.totalPriceWithoutServiceItems?.value ?? 0),
  );
  return str;
};

export const isPickUpItemUnavailable = (
  checkEntryLevel?: boolean,
  subcart?: SubCartDetail,
  entry?: CartEntryDetails,
) => {
  const isUnavailable = checkEntryLevel
    ? entry?.errors?.find((error) => error.code === DeliveryType.Bopis)
    : subcart?.errors?.find((error) => error.code === DeliveryType.Bopis);

  return Boolean(isUnavailable);
};

export const isDeliveryItemUnavailable = (
  checkEntryLevel?: boolean,
  subcart?: SubCartDetail,
  entry?: CartEntryDetails,
) => {
  const isUnavailable = checkEntryLevel
    ? entry?.errors?.find((error) => error.code === DeliveryType.Homedelivery)
    : subcart?.errors?.find((error) => error.code === DeliveryType.Homedelivery);

  return Boolean(isUnavailable);
};

export const isThirdPartyPickUpUnavailable = (
  checkEntryLevel?: boolean,
  subcart?: SubCartDetail,
  entry?: CartEntryDetails,
) => {
  const isUnavailable = checkEntryLevel
    ? entry?.errors?.find((error) => error.code === DeliveryType.Kerry || error.code === DeliveryType.Seveneleven)
    : subcart?.errors?.find((error) => error.code === DeliveryType.Kerry || error.code === DeliveryType.Seveneleven);

  return Boolean(isUnavailable);
};

export const groupedSOPEntries = (allSOPEntries: CartEntryDetails[], sopMetaData?: SopElementResponse[]) => {
  const groupEntries: Record<string, CartEntryDetails[]> = {};
  sopMetaData?.forEach((sopElementData: SopElementResponse) => {
    const data = allSOPEntries.filter((entry) => entry.sopElementId === sopElementData.sopElementId);
    groupEntries[sopElementData.sopElementId] = data;
  });
  return groupEntries;
};

export const isInactiveCart = (amwayUser?: AmwayUserDetails) =>
  amwayUser?.status === AmwayUserDetailsStatusEnum.Inactive;

export const isSopExpired = (
  checkElementLevel?: boolean,
  subcart?: SubCartDetail,
  sopMetaData?: SopElementResponse,
) => {
  const sopExpiredEnum = ExceptionsEnum.Exceptions.SopElementExpired;
  const isExpired = checkElementLevel
    ? sopMetaData?.errors?.find((error) => error.code === sopExpiredEnum)
    : subcart?.errors?.find((error) => error.code === sopExpiredEnum);

  return Boolean(isExpired);
};

export const isInstallmentUnavailable = (cartEntries: CartEntryDetails[] = []) => {
  const isAnonymous = SessionService.getSession().isAnonymous();

  if (isAnonymous) {
    return false;
  }

  const filteredEntries = getFilteredEntries(cartEntries, {
    filterPromotion: false,
    filterSOP: false,
    filterIsAccountFeeProduct: true,
    filterIsAccountKitProduct: true,
  });

  const result = {
    isInstallmentProductAvailable: false,
    isNonInstallmentProductAvailble: false,
  };

  for (const entry of filteredEntries) {
    if (entry.product.flags?.isInstallmentPlanEligible) {
      result.isInstallmentProductAvailable = true;
    }

    if (!entry.partnerPromotionId && !entry.product.flags?.isInstallmentPlanEligible) {
      result.isNonInstallmentProductAvailble = true;
    }
  }

  return result.isInstallmentProductAvailable && result.isNonInstallmentProductAvailble;
};

export const getInstallProductDetails = (cartEntries: CartEntryDetails[] = []): IPProduct[] => {
  const isAnonymous = SessionService.getSession().isAnonymous();
  const filteredEntries = getFilteredEntries(cartEntries, {
    filterPromotion: false,
    filterSOP: false,
    filterIsAccountFeeProduct: true,
    filterIsAccountKitProduct: true,
  });

  if (isAnonymous) {
    return [];
  }
  for (const entry of filteredEntries) {
    if (!entry.partnerPromotionId && !entry.product.flags?.isInstallmentPlanEligible) {
      return [];
    }
  }

  return filteredEntries.map(({ product, quantity, partnerPromotionId, totalPrice }) => ({
    productId: product.id,
    brandName: String(product.brand),
    actualPrice: String(product.basePrice?.value),
    ippFlag: !!product.flags?.isInstallmentPlanEligible,
    name: product.name as string,
    quantity,
    image: product.cartIconPublicUrl as string,
    isPromotionalProduct: Boolean(partnerPromotionId),
    itemValue: totalPrice?.value,
  }));
};

export const getFiredPromotionFromEntries = (cartEntries: CartEntryDetails[] = []) =>
  cartEntries.reduce((acc, entry) => {
    const firedPromotions = entry?.promotionResults?.firedPromotions;
    const { childPromotions } = getBundleEntriesAndChildPromoitions(entry);
    if (firedPromotions) {
      return acc.concat(firedPromotions, childPromotions);
    }

    return acc;
  }, [] as PromotionResult[]);

export const getFiredPromotionFromSOPMetaData = (sopMetaData: SopElementResponse[] = []) =>
  sopMetaData.reduce((acc, sopData) => {
    const firedPromotions = sopData?.promotionResults?.firedPromotions;
    if (firedPromotions) {
      return acc.concat(firedPromotions);
    }
    return acc;
  }, [] as PromotionResult[]);

export const getUniquePromotions = (promos: PromotionResult[], filterAuxiliaryItem?: boolean) => {
  const promoIds: string[] = [];
  return promos.reduce((acc, promo) => {
    if (
      !promoIds.includes(promo.promotion?.promoCode as string) &&
      !promo?.promotion?.hidePromotionInfo &&
      (filterAuxiliaryItem ? !promo.promotion?.hasAuxiliaryItems : true)
    ) {
      acc.push(promo);
      promoIds.push(promo.promotion?.promoCode as string);
    }
    return acc;
  }, [] as PromotionResult[]);
};
